import { Spacing } from '@persona-id/design-tokens';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useBaseStepStyleContext } from 'inquiry/context/BaseStepStyleContext';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import SelfiePictograph from 'verify/components/SelfiePictograph';
import {
  SelfieCaptureOptionDesktop,
  SelfieCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[6]};
`;

const OptionPrimary = styled('div')`
  margin-bottom: ${Spacing[4]};
`;

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

interface InquirySelfieReflectionPromptRouteViewProps {
  currentCaptureOption?: 'web_camera' | 'device_handoff';
  enabledCaptureOptions: Array<SelfieCaptureOptionDesktop | SelfieCaptureOptionMobile>;
  handleDeviceHandoff: () => void;
  handleStartRecording: () => Promise<void>;
  uiReady: boolean;
}

export default function InquirySelfieReflectionPromptRouteView(
  props: InquirySelfieReflectionPromptRouteViewProps,
) {
  const { enabledCaptureOptions } = props;

  if (enabledCaptureOptions.includes('web_camera')) {
    return <RecordView {...props} />;
  } else {
    return <Redirect to='/inquiry/support/camera' />;
  }
}

function RecordView({
  currentCaptureOption,
  enabledCaptureOptions,
  handleDeviceHandoff: handleDeviceHandoff,
  handleStartRecording,
  uiReady,
}: InquirySelfieReflectionPromptRouteViewProps) {
  const { base, buttons } = useBaseStepStyleContext();
  const [t] = useTranslation();

  return (
    <Container>
      <CustomAssetWithFallback alt='header pictograph' />
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{t('selfie.prompt.title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{t('selfie.prompt.prompt-center')}</Markdown>
        </ThemedParagraph>
      </Header>
      <CenteredPictograph height={120} width={120} aria-label='selfie pictograph'>
        <CustomAssetWithFallback alt='selfie center pictograph'>
          <CustomizablePictograph
            url='selfieCenterPictographUrl'
            height='selfieCenterPictographHeight'
            aria-label='selfie center pictograph'
            themed={false}
          >
            <SelfiePictograph>
              <SelfiePictograph.Center />
            </SelfiePictograph>
          </CustomizablePictograph>
        </CustomAssetWithFallback>
      </CenteredPictograph>
      <PositionableButtonContainer>
        <CaptureOptionsContainer>
          <OptionPrimary>
            <Button
              id='selfie-prompt__button--camera'
              data-test='selfie-prompt__button--camera'
              buttonType={ButtonType.Primary}
              disabled={!!currentCaptureOption || !uiReady}
              isPending={currentCaptureOption === 'web_camera'}
              onClick={handleStartRecording}
              styles={buttons?.[ButtonType.Primary]}
            >
              {t('selfie.prompt.btn-submit')}
            </Button>
          </OptionPrimary>
          {enabledCaptureOptions.includes('device_handoff') && (
            <Button
              id='selfie-prompt__button--to-mobile'
              data-test='selfie-prompt__button--to-mobile'
              buttonType={ButtonType.Secondary}
              disabled={!!currentCaptureOption || !uiReady}
              isPending={currentCaptureOption === 'device_handoff'}
              onClick={handleDeviceHandoff}
              styles={buttons?.[ButtonType.Secondary]}
            >
              {t('selfie.prompt.btn-continue-on-device')}
            </Button>
          )}
        </CaptureOptionsContainer>
      </PositionableButtonContainer>
    </Container>
  );
}
